// app/javascript/controllers/wistia_video_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["nextVideoButton", "nextVideoOverlay"]

  static values = {
    code: String,
    session: String,
    node: String,
    lesson: String,
    program: String,
    nextVideoUrl: String,
    completionThreshold: Number
  }

  connect() {
    window._wq = window._wq || [];
    this.initWistia();
  }

  initWistia() {
    window._wq.push({
      id: this.codeValue,
      options: {
        plugin: {
          "captions-v1": { onByDefault: false, language: 'eng' }
        }
      },
      onReady: (video) => {
        this.bindTrackingEvents(video);
        this.dispatchLegacyEvent(video);
      }
    });
  }

  bindTrackingEvents(video) {
    video.bind("secondchange", (t) => {
      if (t % 10 === 0) this.saveActivity(video);
    });

    video.bind('end', () => {
      this.saveActivity(video);
      this.showNextVideoButton();
    });

    video.bind('play', () => {
      this.hideNextVideoButton();
    });

    video.bind('pause', () => {
      this.saveActivity(video);

      if (this.currentProgressPercentage(video) >= this.completionThresholdValue) {
        this.showNextVideoButton();
      }
    });
  }

  async saveActivity(video) {
    const percentageWatched = Math.round((video.time() / video.duration()) * 100);
    const secondsWatched = Math.floor(video.time());
    const payload = {
      activity: {
        node: this.nodeValue,
        lesson: this.lessonValue,
        program_id: this.programValue,
        session: this.sessionValue,
        percentage_watched: percentageWatched,
        seconds_watched: secondsWatched
      }
    };

    try {
      await fetch('/activities', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      console.error("Failed to save activity:", error);
    }
  }

  dispatchLegacyEvent(video) {
    const event = new Event('OnVideoLoaded');
    document.addEventListener('OnVideoLoaded', (e) => {
      e.player = video;
    }, { once: true, capture: true });

    document.dispatchEvent(event);
  }

  showNextVideoButton() {
    if (!this.hasNextVideoButtonTarget) return;
    if (!this.hasNextVideoUrlValue || this.nextVideoUrlValue.length === 0) return;

    this.nextVideoButtonTarget.classList.remove("hidden");
    if (this.hasNextVideoOverlayTarget) this.nextVideoOverlayTarget.classList.remove("hidden");
  }

  hideNextVideoButton() {
    if (!this.hasNextVideoButtonTarget) return;

    this.nextVideoButtonTarget.classList.add("hidden");
    if (this.hasNextVideoOverlayTarget) this.nextVideoOverlayTarget.classList.add("hidden");
  }

  goToNextVideo(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.hasNextVideoUrlValue || this.nextVideoUrlValue.length === 0) return;

    window.location.assign(this.nextVideoUrlValue);
  }

  currentProgressPercentage(video) {
    if (!video || !video.duration || video.duration() <= 0) return 0;

    return Math.round((video.time() / video.duration()) * 100);
  }
}
